import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MIcon } from '../../../lib/micon';
import { apiFetch, ApiError, zeigeApiFehler } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { LdSheet } from '../../../components/ui/LdSheet';
import type { MitarbeitAntwort, MitarbeitFach, MitarbeitWoche } from './profil-api';
import { note, tm, heuteIso } from './format';
import { DiktatFeldKnopf } from './DiktatFeldKnopf';

type Auswahl = 'hj1' | 'hj2' | 'vorjahr';
const ganz = (n: number | null | undefined) => (n == null ? '' : note(n).replace(/,0$/, ''));
const plusTage = (iso: string, n: number) => { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

/** Spalten: Schulwochen einzeln, zusammenhängende Ferienwochen als eine Spalte */
type Spalte = { art: 'woche'; idx: number; w: MitarbeitWoche } | { art: 'ferien'; idx: number; name: string; anzahl: number };
function spalten(wochen: MitarbeitWoche[], bisHeute: boolean): Spalte[] {
  const heute = heuteIso();
  const aus: Spalte[] = [];
  wochen.forEach((w, idx) => {
    if (bisHeute && w.von > heute) return;
    if (w.ferien) {
      const letzte = aus[aus.length - 1];
      if (letzte?.art === 'ferien' && letzte.name === (w.ferien_name ?? 'Ferien')) letzte.anzahl++;
      else aus.push({ art: 'ferien', idx, name: w.ferien_name ?? 'Ferien', anzahl: 1 });
    } else aus.push({ art: 'woche', idx, w });
  });
  while (aus.length && aus[aus.length - 1].art === 'ferien') aus.pop(); // nicht mit Ferien enden
  while (aus.length && aus[0].art === 'ferien') aus.shift();
  return aus;
}

type Editor = { fach: string; w: MitarbeitWoche; anker: DOMRect | null };

/** Welle P1c: Tab Mitarbeit — Wochenraster Fach × Schulwoche (gleiche Quelle wie die Notengrundlage) */
export function MitarbeitTab({ schuelerId, isMobile, halbjahr, schuljahr, onGeaendert }: {
  schuelerId: number; isMobile: boolean; halbjahr: 1 | 2; schuljahr: string; onGeaendert: () => void;
}) {
  const [auswahl, setAuswahl] = useState<Auswahl>(halbjahr === 1 ? 'hj1' : 'hj2');
  const [daten, setDaten] = useState<MitarbeitAntwort[] | null>(null);
  const [fehler, setFehler] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const start = Number(schuljahr.slice(0, 4));
  const vorjahr = `${start - 1}/${String(start).slice(2)}`;

  const laden = useCallback(async () => {
    setFehler(false);
    try {
      const q = auswahl === 'vorjahr'
        ? [`schuljahr=${encodeURIComponent(vorjahr)}&halbjahr=1`, `schuljahr=${encodeURIComponent(vorjahr)}&halbjahr=2`]
        : [`halbjahr=${auswahl === 'hj1' ? 1 : 2}`];
      setDaten(await Promise.all(q.map(x => apiFetch<MitarbeitAntwort>(`/api/schueler/${schuelerId}/mitarbeit?${x}`))));
    } catch { setFehler(true); }
  }, [auswahl, schuelerId, vorjahr]);
  useEffect(() => { setDaten(null); laden(); }, [laden]);

  const geaendert = () => { laden(); onGeaendert(); };
  const laufend = auswahl !== 'vorjahr' && (auswahl === 'hj1' ? 1 : 2) === halbjahr;

  return (
    <section className="ld-pu-karte ld-mt">
      <header className="ld-mt__kopf">
        <h3><MIcon name="back_hand" size={19} />Mündliche Mitarbeit<small>{auswahl === 'vorjahr' ? `Schuljahr ${vorjahr}` : `Halbjahr ${auswahl === 'hj1' ? 1 : 2}`} · eine Beobachtung pro Woche und Fach</small></h3>
        <div className="ld-nt-umschalter" role="group" aria-label="Zeitraum">
          {([['hj1', 'Halbjahr 1'], ['hj2', 'Halbjahr 2'], ['vorjahr', `Schuljahr ${vorjahr}`]] as const).map(([k, l]) => (
            <button key={k} type="button" aria-pressed={auswahl === k} className={auswahl === k ? 'an' : ''} onClick={() => setAuswahl(k)}>{l}</button>
          ))}
        </div>
      </header>
      {fehler && !daten ? <div className="ld-pu-fehler" role="alert"><span>Konnte nicht geladen werden.</span><button type="button" onClick={laden}><MIcon name="refresh" size={15} />Erneut versuchen</button></div>
        : !daten ? <div className="ld-pu-laedt"><span /><span /></div>
        : daten.map(d => (
          <div key={`${d.schuljahr}-${d.halbjahr}`} className="ld-mt__block">
            {daten.length > 1 && <h4 className="ld-mt__unter">Halbjahr {d.halbjahr}</h4>}
            {d.faecher.length === 0 ? <div className="ld-pu-leer">Keine Fächer mit Mitarbeit in diesem Zeitraum.</div>
              : (laufend || auswahl === 'vorjahr' || d.von <= heuteIso()) ? (
                isMobile
                  ? <FachKarten d={d} bisHeute={laufend} onZelle={(fach, w) => setEditor({ fach, w, anker: null })} />
                  : <Raster d={d} bisHeute={laufend} onZelle={(fach, w, el) => setEditor({ fach, w, anker: el.getBoundingClientRect() })} />
              ) : <div className="ld-pu-leer">Halbjahr {d.halbjahr} beginnt am {tm(d.von)}{d.von.slice(0, 4)}.</div>}
          </div>
        ))}
      <div className="ld-mt__legende" aria-hidden="true">
        <span><i className="ld-pu-zelle" />Beobachtung eingetragen</span>
        <span><i className="ld-pu-zelle ld-pu-zelle--luecke" />Lücke: hier fehlt eine Beobachtung</span>
        <span><i className="ld-pu-zelle ld-pu-zelle--jetzt" />Woche läuft noch</span>
        <span><i className="ld-pu-zelle ld-pu-zelle--entsch" />entschuldigt gefehlt (zählt nicht)</span>
        <span><i className="ld-pu-zelle ld-pu-zelle--ferien" />Ferien</span>
      </div>
      {editor && <WochenEditor schuelerId={schuelerId} isMobile={isMobile} editor={editor} onClose={() => setEditor(null)} onGeaendert={geaendert} />}
    </section>
  );
}

function Zelle({ fach, w, onZelle }: { fach: string; w: MitarbeitWoche; onZelle: (fach: string, w: MitarbeitWoche, el: HTMLElement) => void }) {
  const heute = heuteIso();
  const diese = w.von <= heute && heute < plusTage(w.von, 7);
  const titel = `${fach}, KW ${w.kw}`;
  if (w.status === 'entschuldigt') return (
    <button type="button" className="ld-pu-zelle ld-pu-zelle--entsch ld-mt__zelle" onClick={e => onZelle(fach, w, e.currentTarget)} aria-label={`${titel}: entschuldigt gefehlt, zählt nicht`}>{w.wert != null ? ganz(w.wert) : 'e'}</button>
  );
  const leer = w.wert == null;
  const klasse = `ld-pu-zelle ld-mt__zelle${leer ? (w.status === 'luecke' ? ' ld-pu-zelle--luecke' : ' ld-mt__zelle--frei') : ''}${diese ? ' ld-pu-zelle--jetzt' : ''}`;
  return (
    <button type="button" className={klasse} onClick={e => onZelle(fach, w, e.currentTarget)}
      aria-label={leer ? `${titel}: noch keine Beobachtung — eintragen` : `${titel}: ${ganz(w.wert)}${w.tendenz ?? ''} — ändern`}>
      {leer ? '+' : <>{ganz(w.wert)}{w.tendenz && <sup>{w.tendenz === '-' ? '−' : '+'}</sup>}</>}
    </button>
  );
}

/** Mini-Verlauf: x = Lage der Woche (Ferien als Lücke), y = Spanne der Werte (nicht ab 0) */
function MiniVerlauf({ f, sp }: { f: MitarbeitFach; sp: Spalte[] }) {
  const pos = new Map<string, number>();
  let x = 0;
  for (const s of sp) { if (s.art === 'woche') pos.set(s.w.von, x); x += s.art === 'ferien' ? 1 : 1; }
  const pkt = (f.verlauf ?? []).filter(v => v.schnitt != null && pos.has(v.von)).map(v => ({ x: pos.get(v.von)!, y: v.schnitt! }));
  if (pkt.length < 2) return <span className="ld-mt__mini" />;
  const xs = Math.max(1, x - 1), ymin = Math.min(...pkt.map(p => p.y)), ymax = Math.max(...pkt.map(p => p.y));
  const spanne = Math.max(0.4, ymax - ymin);
  const W = 64, Hh = 22;
  // bessere Note (kleiner) oben
  const d = pkt.map((p, i) => `${i ? 'L' : 'M'}${((p.x / xs) * W).toFixed(1)},${(2 + ((p.y - ymin) / spanne) * (Hh - 4)).toFixed(1)}`).join(' ');
  const trend = pkt[pkt.length - 1].y - pkt[0].y;
  return (
    <svg className={`ld-mt__mini ld-mt__mini--${Math.abs(trend) < 0.1 ? 'gleich' : trend < 0 ? 'steigend' : 'fallend'}`} viewBox={`0 0 ${W} ${Hh}`} aria-hidden="true">
      <path d={d} fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Raster({ d, bisHeute, onZelle }: { d: MitarbeitAntwort; bisHeute: boolean; onZelle: (fach: string, w: MitarbeitWoche, el: HTMLElement) => void }) {
  const sp = spalten(d.faecher[0].wochen, bisHeute);
  const heute = heuteIso();
  const vorlage = sp.map(s => s.art === 'ferien' ? `minmax(${Math.min(2, s.anzahl) * 38}px, ${s.anzahl}fr)` : 'minmax(38px, 1fr)').join(' ');
  return (
    <div className="ld-mt__scroll">
      <div className="ld-mt__raster" style={{ gridTemplateColumns: `112px ${vorlage} 104px` }} role="table" aria-label={`Mitarbeit Halbjahr ${d.halbjahr}`}>
        <span role="columnheader" />
        {sp.map(s => s.art === 'ferien'
          ? <span key={`f${s.idx}`} role="columnheader" className="ld-mt__kw ld-mt__kw--ferien">{s.name}</span>
          : <span key={s.w.von} role="columnheader" className={`ld-mt__kw${s.w.von <= heute && heute < plusTage(s.w.von, 7) ? ' ld-mt__kw--jetzt' : ''}`}>KW {s.w.kw}<small>{s.w.von <= heute && heute < plusTage(s.w.von, 7) ? 'diese Woche' : tm(s.w.von)}</small></span>)}
        <span role="columnheader" className="ld-mt__kw">Ø · Verlauf</span>
        {d.faecher.map(f => (
          <React.Fragment key={f.fach}>
            <span role="rowheader" className="ld-mt__fach"><b>{f.fach}</b><small>{f.wochen_beobachtet} von {f.wochen_gesamt} Wochen</small></span>
            {sp.map(s => s.art === 'ferien'
              ? <span key={`f${s.idx}`} className="ld-pu-zelle ld-pu-zelle--ferien ld-mt__zelle" role="cell" aria-label={s.name} />
              : <span key={s.w.von} role="cell" className="ld-mt__cell"><Zelle fach={f.fach} w={f.wochen[s.idx]} onZelle={onZelle} /></span>)}
            <span role="cell" className="ld-mt__schnitt"><MiniVerlauf f={f} sp={sp} /><b>{f.wochen_beobachtet > 0 ? note(f.schnitt) : '—'}</b></span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function FachKarten({ d, bisHeute, onZelle }: { d: MitarbeitAntwort; bisHeute: boolean; onZelle: (fach: string, w: MitarbeitWoche, el: HTMLElement) => void }) {
  return (
    <div className="ld-mt__karten">
      {d.faecher.map(f => {
        const alle = spalten(f.wochen, bisHeute);
        // die letzten 6 Schulwochen, Ferien dazwischen bleiben sichtbar
        let n = 0; let ab = alle.length;
        for (let i = alle.length - 1; i >= 0 && n < 6; i--) { ab = i; if (alle[i].art === 'woche') n++; }
        const sp = alle.slice(ab);
        return (
          <div key={f.fach} className="ld-mt__karte">
            <div className="ld-mt__karte-kopf"><b>{f.fach}</b><span>{f.wochen_beobachtet} von {f.wochen_gesamt} Wochen</span><b className="ld-mt__karte-schnitt">{f.wochen_beobachtet > 0 ? note(f.schnitt) : '—'}</b></div>
            <div className="ld-mt__karte-reihe">
              {sp.map(s => s.art === 'ferien'
                ? <span key={`f${s.idx}`} className="ld-mt__karte-zelle"><small>Ferien</small><span className="ld-pu-zelle ld-pu-zelle--ferien ld-mt__zelle" /></span>
                : <span key={s.w.von} className="ld-mt__karte-zelle"><small>KW {s.w.kw}</small><Zelle fach={f.fach} w={s.w} onZelle={onZelle} /></span>)}
            </div>
            {f.anzahl_luecken > 0 && <div className="ld-pu-hinweis"><MIcon name="info" size={15} />{f.anzahl_luecken === 1 ? '1 Woche' : `${f.anzahl_luecken} Wochen`} ohne Beobachtung</div>}
          </div>
        );
      })}
    </div>
  );
}

/** Kleines Fenster (PC, am Feld) bzw. Sheet (Handy): 1–6, Tendenz, Bemerkung mit Mikrofon */
function WochenEditor({ schuelerId, isMobile, editor, onClose, onGeaendert }: { schuelerId: number; isMobile: boolean; editor: Editor; onClose: () => void; onGeaendert: () => void }) {
  const { fach, w } = editor;
  const gefuellt = (w.ids?.length ?? 0) > 0;
  const [wert, setWert] = useState<number | null>(gefuellt && w.wert != null && Number.isInteger(w.wert) ? w.wert : null);
  const [tendenz, setTendenz] = useState<'+' | '-' | null>((w.tendenz as '+' | '-' | null) ?? null);
  const [bemerkung, setBemerkung] = useState(w.bemerkung ?? '');
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState('');
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (isMobile || !editor.anker || !box.current) return;
    const a = editor.anker, b = box.current.getBoundingClientRect();
    const left = Math.min(window.innerWidth - b.width - 12, Math.max(12, a.left + a.width / 2 - b.width / 2));
    const top = a.bottom + 8 + b.height < window.innerHeight ? a.bottom + 8 : Math.max(12, a.top - b.height - 8);
    setPos({ top, left });
  }, [editor, isMobile]);
  useEffect(() => {
    if (isMobile) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [isMobile, onClose]);

  async function eintragen() {
    if (wert == null) return;
    setLaeuft(true); setMeldung('');
    try {
      await apiFetch(`/api/schueler/${schuelerId}/mitarbeit`, { method: 'POST', body: JSON.stringify({ fach, woche: w.von, wert, tendenz, bemerkung: bemerkung.trim() || null, ersetzen: gefuellt }) });
      toast(`Mitarbeit ${fach}, KW ${w.kw} ${gefuellt ? 'geändert' : 'eingetragen'}`, 'success');
      onGeaendert(); onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) setMeldung(err.message); else zeigeApiFehler(err, 'Speichern fehlgeschlagen');
    } finally { setLaeuft(false); }
  }
  async function loeschen() {
    const alt = w.einzel ?? [];
    setLaeuft(true);
    try {
      for (const e of alt) await apiFetch(`/api/mitarbeit/${e.id}`, { method: 'DELETE' });
      onGeaendert(); onClose();
      toast(`Mitarbeit ${fach}, KW ${w.kw} gelöscht`, 'success', { dauerMs: 6000, aktion: { label: 'Rückgängig', onClick: async () => {
        try {
          if (alt.length === 1) {
            await apiFetch(`/api/schueler/${schuelerId}/mitarbeit`, { method: 'POST', body: JSON.stringify({ fach, woche: w.von, wert: alt[0].note, tendenz: alt[0].tendenz, bemerkung: alt[0].kontext, ersetzen: true }) });
          } else {
            for (const e of alt) await apiFetch('/api/mitarbeit', { method: 'POST', body: JSON.stringify({ schueler_id: schuelerId, fach, datum: w.von, note: e.note, kontext: e.kontext, quelle: 'manuell' }) });
          }
          onGeaendert();
        } catch (err) { zeigeApiFehler(err, 'Rückgängig fehlgeschlagen'); }
      } } });
    } catch (err) { zeigeApiFehler(err, 'Löschen fehlgeschlagen'); }
    finally { setLaeuft(false); }
  }

  const inhalt = (
    <div className="ld-mte">
      <div className="ld-mte__kopf">
        <div><b>{fach} · KW {w.kw}</b><span>{tm(w.von)}–{tm(plusTage(w.von, 4))} · {w.status === 'entschuldigt' ? 'entschuldigt gefehlt, zählt nicht' : gefuellt ? `bisher ${note(w.wert)}${(w.eintraege ?? 0) > 1 ? ` (${w.eintraege} Einträge)` : ''}` : 'noch keine Beobachtung'}</span></div>
        {isMobile && <button type="button" className="ld-pk-icon ld-pk-icon--klein" aria-label="Schließen" onClick={onClose}><MIcon name="close" size={18} /></button>}
      </div>
      <div className="ld-mte__werte" role="group" aria-label="Wert">
        {[1, 2, 3, 4, 5, 6].map(n => <button key={n} type="button" aria-pressed={wert === n} className={`ld-mws__wert${wert === n ? ' an' : ''}`} onClick={() => setWert(n)}>{n}</button>)}
      </div>
      <div className="ld-mws__tendenz" role="group" aria-label="Tendenz">
        {([['+', 'Tendenz +'], [null, 'ohne'], ['-', 'Tendenz −']] as const).map(([t, l]) => (
          <button key={l} type="button" aria-pressed={tendenz === t} className={`ld-mws__chip${tendenz === t ? ' an' : ''}`} onClick={() => setTendenz(t)}>{l}</button>
        ))}
      </div>
      <div className="ld-mte__bemerkung">
        <input value={bemerkung} onChange={e => setBemerkung(e.target.value)} placeholder="Bemerkung (optional)" aria-label="Bemerkung" />
        <DiktatFeldKnopf onText={t => setBemerkung(b => (b ? `${b} ${t}` : t))} label="Bemerkung erzählen" />
      </div>
      {meldung && <div className="ld-mws__meldung" role="alert">{meldung}</div>}
      <div className="ld-mte__knoepfe">
        {gefuellt && <button type="button" className="ld-mte__loeschen" onClick={loeschen} disabled={laeuft} aria-label="Beobachtung dieser Woche löschen"><MIcon name="delete" size={17} />Löschen</button>}
        <button type="button" className="ld-mte__abbrechen" onClick={onClose}>Abbrechen</button>
        <button type="button" className="ld-mte__eintragen" onClick={eintragen} disabled={wert == null || laeuft}><MIcon name="check" size={17} />{gefuellt ? 'Ändern' : 'Eintragen'}</button>
      </div>
    </div>
  );
  if (isMobile) return <LdSheet open onClose={onClose} maxWidth={520} ariaLabel={`Mitarbeit ${fach}, KW ${w.kw}`}>{inhalt}</LdSheet>;
  return createPortal(
    <>
      <div className="ld-mte__hinter" onClick={onClose} />
      <div ref={box} role="dialog" aria-modal="true" aria-label={`Mitarbeit ${fach}, KW ${w.kw}`} className="ld-mte__popover" style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden', top: 0, left: 0 }}>{inhalt}</div>
    </>, document.body);
}
